/**
 * Swordsman Slash Effect - Sword slash visual effects for melee attacks
 * Features:
 * - Arc slash trail following sword swing
 * - Spark particles on impact
 * - Ground dust effect
 * - Energy wave on powerful attacks
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';

interface SlashArc {
  x: number;
  y: number;
  z: number;
  rotation: number;  // Direction the attacker is facing
  age: number;
  maxAge: number;
  phase: number;     // Animation phase
}

interface SparkParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  maxAge: number;
  size: number;
}

interface DustCloud {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  scale: number;
}

interface EnergyWave {
  x: number;
  y: number;
  z: number;
  rotation: number;
  age: number;
  maxAge: number;
}

const MAX_SLASH_ARCS = 100;
const MAX_SPARKS = 500;
const MAX_DUST_CLOUDS = 50;
const MAX_ENERGY_WAVES = 30;

// Shader for slash arc
const slashVertexShader = `
  attribute float alpha;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    vAlpha = alpha;
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const slashFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  uniform float time;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    // Arc gradient - brighter at the edge
    float edgeBrightness = pow(vUv.x, 0.5);

    // Fade based on progress
    float fade = 1.0 - progress;
    fade = pow(fade, 0.5);

    // Add shimmer effect
    float shimmer = sin(vUv.x * 20.0 - time * 30.0) * 0.1 + 0.9;

    // Core color with white edge
    vec3 coreColor = mix(color, vec3(1.0), edgeBrightness * 0.6);

    float alpha = edgeBrightness * fade * shimmer * vAlpha;

    gl_FragColor = vec4(coreColor, alpha);
  }
`;

// Shader for sparks
const sparkVertexShader = `
  attribute float size;
  attribute float alpha;
  varying float vAlpha;

  void main() {
    vAlpha = alpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const sparkFragmentShader = `
  uniform vec3 color;
  varying float vAlpha;

  void main() {
    // Circular point with soft edges
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;

    float alpha = (1.0 - dist * 2.0) * vAlpha;

    // Hot white core
    vec3 finalColor = mix(color, vec3(1.0, 1.0, 0.9), 1.0 - dist * 2.0);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader for dust clouds
const dustVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const dustFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Dust expands outward
    float ring = 1.0 - smoothstep(progress * 0.8, progress, dist);
    ring *= smoothstep(0.0, progress * 0.3, dist);

    // Fade out
    float fade = 1.0 - progress;

    float alpha = ring * fade * 0.4;

    gl_FragColor = vec4(color, alpha);
  }
`;

// Shader for energy wave
const waveVertexShader = `
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    vUv = uv;
    vHeight = position.y;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const waveFragmentShader = `
  uniform float progress;
  uniform float time;
  uniform vec3 color;
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    // Wave travels forward
    float wavePos = progress;
    float waveWidth = 0.15;

    // Distance from wave front
    float waveDist = abs(vUv.y - wavePos);
    float wave = 1.0 - smoothstep(0.0, waveWidth, waveDist);

    // Vertical fade
    float vertFade = 1.0 - abs(vHeight) * 2.0;
    vertFade = max(vertFade, 0.0);

    // Overall fade
    float fade = 1.0 - progress;

    // Shimmer
    float shimmer = sin(vUv.y * 30.0 - time * 20.0) * 0.15 + 0.85;

    float alpha = wave * vertFade * fade * shimmer * 0.7;

    // White core
    vec3 finalColor = mix(color, vec3(1.0), wave * 0.4);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

export class SwordsmanSlashEffect {
  private scene: THREE.Scene;
  private time = 0;

  // Slash arcs
  private slashArcs: SlashArc[] = [];
  private slashGeometry: THREE.BufferGeometry;
  private slashMaterials: THREE.ShaderMaterial[] = [];
  private slashMeshes: THREE.Mesh[] = [];

  // Spark particles
  private sparks: SparkParticle[] = [];
  private sparkGeometry: THREE.BufferGeometry;
  private sparkMaterial: THREE.ShaderMaterial;
  private sparkPoints: THREE.Points;
  private sparkPositions: Float32Array;
  private sparkSizes: Float32Array;
  private sparkAlphas: Float32Array;

  // Dust clouds
  private dustClouds: DustCloud[] = [];
  private dustGeometry: THREE.PlaneGeometry;
  private dustMaterials: THREE.ShaderMaterial[] = [];
  private dustMeshes: THREE.Mesh[] = [];

  // Energy waves
  private energyWaves: EnergyWave[] = [];
  private waveGeometry: THREE.PlaneGeometry;
  private waveMaterials: THREE.ShaderMaterial[] = [];
  private waveMeshes: THREE.Mesh[] = [];

  // Colors
  private slashColor = new THREE.Color(0xffffff);    // White slash
  private sparkColor = new THREE.Color(0xffaa00);    // Orange sparks
  private dustColor = new THREE.Color(0x8b7355);     // Brown dust
  private waveColor = new THREE.Color(0x88ccff);     // Light blue energy

  // Event handler
  private attackEventHandler: ((data: {
    attackerId: number;
    targetId: number;
    damage: number;
  }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Initialize slash arc geometry (curved plane)
    this.slashGeometry = this.createSlashGeometry();

    // Pre-create slash meshes
    for (let i = 0; i < MAX_SLASH_ARCS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: slashVertexShader,
        fragmentShader: slashFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.slashColor },
          time: { value: 0 },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.slashMaterials.push(material);

      const mesh = new THREE.Mesh(this.slashGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.slashMeshes.push(mesh);
    }

    // Initialize spark particles
    this.sparkPositions = new Float32Array(MAX_SPARKS * 3);
    this.sparkSizes = new Float32Array(MAX_SPARKS);
    this.sparkAlphas = new Float32Array(MAX_SPARKS);

    this.sparkGeometry = new THREE.BufferGeometry();
    this.sparkGeometry.setAttribute('position', new THREE.BufferAttribute(this.sparkPositions, 3));
    this.sparkGeometry.setAttribute('size', new THREE.BufferAttribute(this.sparkSizes, 1));
    this.sparkGeometry.setAttribute('alpha', new THREE.BufferAttribute(this.sparkAlphas, 1));

    this.sparkMaterial = new THREE.ShaderMaterial({
      vertexShader: sparkVertexShader,
      fragmentShader: sparkFragmentShader,
      uniforms: {
        color: { value: this.sparkColor },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.sparkPoints = new THREE.Points(this.sparkGeometry, this.sparkMaterial);
    this.sparkPoints.frustumCulled = false;
    this.scene.add(this.sparkPoints);

    // Initialize dust geometry
    this.dustGeometry = new THREE.PlaneGeometry(1, 1);

    // Pre-create dust meshes
    for (let i = 0; i < MAX_DUST_CLOUDS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: dustVertexShader,
        fragmentShader: dustFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.dustColor },
        },
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.dustMaterials.push(material);

      const mesh = new THREE.Mesh(this.dustGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.dustMeshes.push(mesh);
    }

    // Initialize wave geometry (vertical plane)
    this.waveGeometry = new THREE.PlaneGeometry(2, 1, 16, 8);

    // Pre-create wave meshes
    for (let i = 0; i < MAX_ENERGY_WAVES; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: waveVertexShader,
        fragmentShader: waveFragmentShader,
        uniforms: {
          progress: { value: 0 },
          time: { value: 0 },
          color: { value: this.waveColor },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.waveMaterials.push(material);

      const mesh = new THREE.Mesh(this.waveGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.waveMeshes.push(mesh);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  private createSlashGeometry(): THREE.BufferGeometry {
    // Create a curved slash arc geometry
    const segments = 16;
    const arcAngle = Math.PI * 0.6;  // 108 degree arc
    const innerRadius = 0.1;
    const outerRadius = 0.6;

    const vertices: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const alphas: number[] = [];

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = -arcAngle / 2 + t * arcAngle;

      // Inner vertex
      vertices.push(
        Math.sin(angle) * innerRadius,
        Math.cos(angle) * innerRadius,
        0
      );
      uvs.push(0, t);
      alphas.push(0.3);  // Inner is dimmer

      // Outer vertex
      vertices.push(
        Math.sin(angle) * outerRadius,
        Math.cos(angle) * outerRadius,
        0
      );
      uvs.push(1, t);
      alphas.push(1.0);  // Outer is brighter
    }

    // Create triangles
    for (let i = 0; i < segments; i++) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2);
      indices.push(base + 1, base + 3, base + 2);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphas, 1));
    geometry.setIndex(indices);

    return geometry;
  }

  private setupEventListeners(): void {
    // Listen for sword:slash events
    this.attackEventHandler = (data: {
      attackerId: number;
      targetId: number;
      damage: number;
    }) => {
      // We need position data - this will come from the event system
      // For now, we'll spawn effects at a calculated position
    };

    // Listen for sword slash event (custom event for swordsman)
    EventBus.on('sword:slash', (data: {
      x: number;
      y: number;
      z: number;
      rotation: number;
      targetX: number;
      targetY: number;
      targetZ: number;
      damage: number;
    }) => {
      // Spawn slash arc at attacker position
      this.spawnSlashArc(data.x, data.y + 0.5, data.z, data.rotation);

      // Spawn sparks at impact point (between attacker and target)
      const impactX = (data.x + data.targetX) / 2;
      const impactY = (data.y + data.targetY) / 2 + 0.4;
      const impactZ = (data.z + data.targetZ) / 2;
      this.spawnSparks(impactX, impactY, impactZ, 8 + Math.floor(data.damage / 5));

      // Spawn dust at ground level
      this.spawnDustCloud(data.targetX, data.targetY + 0.05, data.targetZ);

      // Spawn energy wave for high damage attacks
      if (data.damage > 15) {
        this.spawnEnergyWave(data.x, data.y + 0.3, data.z, data.rotation);
      }
    });
  }

  private spawnSlashArc(x: number, y: number, z: number, rotation: number): void {
    if (this.slashArcs.length >= MAX_SLASH_ARCS) {
      this.slashArcs.shift();
    }

    this.slashArcs.push({
      x,
      y,
      z,
      rotation,
      age: 0,
      maxAge: 0.25,
      phase: 0,
    });
  }

  private spawnSparks(x: number, y: number, z: number, count: number): void {
    for (let i = 0; i < count && this.sparks.length < MAX_SPARKS; i++) {
      // Random direction with bias forward and up
      const angle = Math.random() * Math.PI * 2;
      const upBias = 0.3 + Math.random() * 0.5;
      const speed = 2 + Math.random() * 3;

      this.sparks.push({
        x,
        y,
        z,
        vx: Math.cos(angle) * speed * (1 - upBias),
        vy: speed * upBias + Math.random() * 2,
        vz: Math.sin(angle) * speed * (1 - upBias),
        age: 0,
        maxAge: 0.3 + Math.random() * 0.4,
        size: 3 + Math.random() * 4,
      });
    }
  }

  private spawnDustCloud(x: number, y: number, z: number): void {
    if (this.dustClouds.length >= MAX_DUST_CLOUDS) {
      this.dustClouds.shift();
    }

    this.dustClouds.push({
      x,
      y,
      z,
      age: 0,
      maxAge: 0.5,
      scale: 0.8 + Math.random() * 0.4,
    });
  }

  private spawnEnergyWave(x: number, y: number, z: number, rotation: number): void {
    if (this.energyWaves.length >= MAX_ENERGY_WAVES) {
      this.energyWaves.shift();
    }

    this.energyWaves.push({
      x,
      y,
      z,
      rotation,
      age: 0,
      maxAge: 0.4,
    });
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update slash arcs
    for (let i = this.slashArcs.length - 1; i >= 0; i--) {
      const arc = this.slashArcs[i];
      arc.age += deltaTime;

      if (arc.age >= arc.maxAge) {
        this.slashArcs.splice(i, 1);
        continue;
      }

      const mesh = this.slashMeshes[i];
      const material = this.slashMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(arc.x, arc.y, arc.z);
        mesh.rotation.y = arc.rotation;

        // Animate the slash
        const progress = arc.age / arc.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;

        // Scale up as it swings
        const swingProgress = Math.min(progress * 2, 1);
        mesh.scale.setScalar(0.5 + swingProgress * 0.5);

        // Rotate through the swing
        mesh.rotation.z = -0.3 + progress * 0.8;
      }
    }

    // Hide unused slash meshes
    for (let i = this.slashArcs.length; i < MAX_SLASH_ARCS; i++) {
      this.slashMeshes[i].visible = false;
    }

    // Update sparks
    const gravity = -15;
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      spark.age += deltaTime;

      if (spark.age >= spark.maxAge) {
        this.sparks.splice(i, 1);
        continue;
      }

      // Apply physics
      spark.vy += gravity * deltaTime;
      spark.x += spark.vx * deltaTime;
      spark.y += spark.vy * deltaTime;
      spark.z += spark.vz * deltaTime;

      // Bounce off ground
      if (spark.y < 0.05) {
        spark.y = 0.05;
        spark.vy = -spark.vy * 0.3;
        spark.vx *= 0.5;
        spark.vz *= 0.5;
      }
    }

    // Update spark geometry
    for (let i = 0; i < MAX_SPARKS; i++) {
      if (i < this.sparks.length) {
        const spark = this.sparks[i];
        const progress = spark.age / spark.maxAge;

        this.sparkPositions[i * 3] = spark.x;
        this.sparkPositions[i * 3 + 1] = spark.y;
        this.sparkPositions[i * 3 + 2] = spark.z;
        this.sparkSizes[i] = spark.size * (1 - progress * 0.5);
        this.sparkAlphas[i] = 1 - progress;
      } else {
        this.sparkAlphas[i] = 0;
      }
    }

    this.sparkGeometry.attributes.position.needsUpdate = true;
    this.sparkGeometry.attributes.size.needsUpdate = true;
    this.sparkGeometry.attributes.alpha.needsUpdate = true;

    // Update dust clouds
    for (let i = this.dustClouds.length - 1; i >= 0; i--) {
      const dust = this.dustClouds[i];
      dust.age += deltaTime;

      if (dust.age >= dust.maxAge) {
        this.dustClouds.splice(i, 1);
        continue;
      }

      const mesh = this.dustMeshes[i];
      const material = this.dustMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(dust.x, dust.y, dust.z);

        const progress = dust.age / dust.maxAge;
        const scale = dust.scale * (1 + progress * 2);
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
      }
    }

    // Hide unused dust meshes
    for (let i = this.dustClouds.length; i < MAX_DUST_CLOUDS; i++) {
      this.dustMeshes[i].visible = false;
    }

    // Update energy waves
    for (let i = this.energyWaves.length - 1; i >= 0; i--) {
      const wave = this.energyWaves[i];
      wave.age += deltaTime;

      if (wave.age >= wave.maxAge) {
        this.energyWaves.splice(i, 1);
        continue;
      }

      const mesh = this.waveMeshes[i];
      const material = this.waveMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(wave.x, wave.y, wave.z);
        mesh.rotation.y = wave.rotation;

        const progress = wave.age / wave.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;

        // Wave expands forward
        const forwardOffset = progress * 2;
        mesh.position.x += Math.sin(wave.rotation) * forwardOffset;
        mesh.position.z += Math.cos(wave.rotation) * forwardOffset;
      }
    }

    // Hide unused wave meshes
    for (let i = this.energyWaves.length; i < MAX_ENERGY_WAVES; i++) {
      this.waveMeshes[i].visible = false;
    }
  }

  dispose(): void {
    // Dispose slash arcs
    for (const mesh of this.slashMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.slashMaterials) {
      material.dispose();
    }
    this.slashGeometry.dispose();
    this.slashArcs = [];

    // Dispose sparks
    this.scene.remove(this.sparkPoints);
    this.sparkGeometry.dispose();
    this.sparkMaterial.dispose();
    this.sparks = [];

    // Dispose dust clouds
    for (const mesh of this.dustMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.dustMaterials) {
      material.dispose();
    }
    this.dustGeometry.dispose();
    this.dustClouds = [];

    // Dispose energy waves
    for (const mesh of this.waveMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.waveMaterials) {
      material.dispose();
    }
    this.waveGeometry.dispose();
    this.energyWaves = [];
  }
}
